import { useId } from 'react';

import {
  ConfirmSheet,
  ConfirmSheetAction,
  ConfirmSheetCancel,
  ConfirmSheetContent,
  ConfirmSheetDescription,
  ConfirmSheetFooter,
  ConfirmSheetTitle,
} from '@/components/ui/sheet';

type ConfirmMessageSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
  /** Renders the "Don't show this again" opt-out. */
  showSuppress?: boolean;
  suppressed?: boolean;
  onSuppressChange?: (value: boolean) => void;
};

/**
 * Blocking confirmation used when a second message type would replace the
 * first (GIFTWEB-25 AC 3). Bottom sheet under 1024px, centred modal above —
 * both come from ConfirmSheet.
 */
function ConfirmMessageSheet({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  showSuppress = false,
  suppressed = false,
  onSuppressChange,
}: ConfirmMessageSheetProps) {
  const suppressId = useId();

  return (
    <ConfirmSheet open={open} onOpenChange={onOpenChange}>
      <ConfirmSheetContent data-testid='confirm-message-sheet'>
        <ConfirmSheetTitle className='text-xl leading-tight font-bold tracking-[-0.01em] text-[#1a201d]'>
          {title}
        </ConfirmSheetTitle>
        <ConfirmSheetDescription className='text-[14px] leading-[1.55] text-[#4a534d]'>
          {description}
        </ConfirmSheetDescription>

        {showSuppress && (
          <label
            htmlFor={suppressId}
            className='flex cursor-pointer items-center gap-2.5 text-[13px] text-[#4a534d]'
          >
            <input
              id={suppressId}
              type='checkbox'
              checked={suppressed}
              onChange={(e) => onSuppressChange?.(e.target.checked)}
              data-testid='confirm-message-suppress'
              className='size-[18px] shrink-0 cursor-pointer accent-[#256956]'
            />
            Don't show this again
          </label>
        )}

        <ConfirmSheetFooter>
          <ConfirmSheetCancel data-testid='confirm-message-cancel'>
            Cancel
          </ConfirmSheetCancel>
          <ConfirmSheetAction
            data-testid='confirm-message-confirm'
            onClick={onConfirm}
          >
            {confirmLabel}
          </ConfirmSheetAction>
        </ConfirmSheetFooter>
      </ConfirmSheetContent>
    </ConfirmSheet>
  );
}

export default ConfirmMessageSheet;
